import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Bus, Train, Car, MapPin, Clock, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/data/translations";

const TransportationInfo = () => {
  const { language } = useLanguage();

  const publicTransportSchedule = [
    {
      destination: "Interlaken",
      method: "Bus 21",
      duration: "25 min",
      frequencyKey: "every30min",
    },
    {
      destination: "Thun",
      method: "Bus + Train",
      duration: "45 min",
      frequencyKey: "everyHour",
    },
    {
      destination: "Grindelwald",
      method: "Bus 122",
      duration: "1 hour",
      frequencyKey: "every2Hours",
    },
    {
      destination: "Lauterbrunnen",
      method: "Bus 141",
      duration: "1.5 hours",
      frequencyKey: "every2Hours",
    },
    {
      destination: "Bern",
      method: "Train",
      duration: "1 hour",
      frequencyKey: "every30min",
    },
    {
      destination: "Zurich Airport",
      method: "Train",
      duration: "2.5 hours",
      frequencyKey: "everyHour",
    },
  ];

  const shuttleServices = [
    { serviceKey: "skiShuttle", frequencyKey: "asNeeded" },
    { serviceKey: "airportTransfer", frequencyKey: "daily24_7" },
    { serviceKey: "cityTransfer", frequencyKey: "onDemand" },
    { serviceKey: "attractionShuttle", frequencyKey: "scheduled" },
  ];

  return (
    <section className="pt-32 pb-16 bg-white relative" data-oid="99v774i">
      {/* Top gradient transition from #F6F2EC to white */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, #F6F2EC 0%, transparent 100%)",
        }}
        data-oid="dfs__mu"
      />

      <div className="container mx-auto px-6 relative z-10" data-oid="ixjsx:5">
        <div className="text-center mb-12" data-oid="jo2yxg:">
          <h2
            className="text-3xl md:text-4xl text-gray-900 font-playfair mb-4"
            data-oid="l95eg5x"
          >
            {getTranslation(language, "localAttractions.transportation.title")}
          </h2>
          <p
            className="text-lg text-gray-600 font-lato max-w-3xl mx-auto"
            data-oid="_l72rgm"
          >
            {getTranslation(
              language,
              "localAttractions.transportation.description",
            )}
          </p>
        </div>

        <Accordion
          type="single"
          collapsible
          className="w-full space-y-4"
          data-oid="kr8r1ko"
        >
          <AccordionItem
            value="public-transport"
            className="bg-[#F6F2EC] rounded-lg border border-gray-200"
            data-oid="fa2d2s."
          >
            <AccordionTrigger
              className="px-6 py-4 hover:no-underline"
              data-oid="2hocfcn"
            >
              <div className="flex items-center gap-3" data-oid="3p9v:nb">
                <Bus className="w-5 h-5 text-[#A38648]" data-oid="rqdh8dp" />
                <span
                  className="font-semibold text-gray-900"
                  data-oid="exeth16"
                >
                  {getTranslation(
                    language,
                    "localAttractions.transportation.publicTransport",
                  )}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6" data-oid="gkadl2k">
              <div className="space-y-6" data-oid="nm0dss2">
                <div data-oid="-bf62wg">
                  <h4
                    className="font-semibold text-gray-900 mb-3 flex items-center gap-2"
                    data-oid="8kbxgk-"
                  >
                    <MapPin className="w-4 h-4" data-oid="l-cmvoi" />
                    {getTranslation(
                      language,
                      "localAttractions.transportation.keyRoutes",
                    )}
                  </h4>
                  <div className="overflow-x-auto" data-oid="svsuygg">
                    <Table data-oid="184yrso">
                      <TableHeader data-oid="327he8b">
                        <TableRow data-oid="r3s8uzy">
                          <TableHead
                            className="font-semibold text-gray-900"
                            data-oid="56c.n63"
                          >
                            {getTranslation(
                              language,
                              "localAttractions.transportation.destination",
                            )}
                          </TableHead>
                          <TableHead
                            className="font-semibold text-gray-900"
                            data-oid="q3ck7y."
                          >
                            {getTranslation(
                              language,
                              "localAttractions.transportation.method",
                            )}
                          </TableHead>
                          <TableHead
                            className="font-semibold text-gray-900"
                            data-oid="vhlveao"
                          >
                            {getTranslation(
                              language,
                              "localAttractions.transportation.duration",
                            )}
                          </TableHead>
                          <TableHead
                            className="font-semibold text-gray-900"
                            data-oid="4js87ev"
                          >
                            {getTranslation(
                              language,
                              "localAttractions.transportation.frequency",
                            )}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-oid="yx6azuy">
                        {publicTransportSchedule.map((route, index) => (
                          <TableRow key={index} data-oid="r04uhvg">
                            <TableCell
                              className="font-medium"
                              data-oid="4q95.ez"
                            >
                              {route.destination}
                            </TableCell>
                            <TableCell data-oid="br20vuu">
                              {route.method}
                            </TableCell>
                            <TableCell data-oid="5byb._2">
                              {route.duration}
                            </TableCell>
                            <TableCell data-oid=":3t1cb0">
                              {getTranslation(
                                language,
                                `localAttractions.transportation.frequencies.${route.frequencyKey}`,
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg" data-oid="2kr8wpo">
                  <h4
                    className="font-semibold text-gray-900 mb-2 flex items-center gap-2"
                    data-oid="szhv:-h"
                  >
                    <Badge
                      className="bg-blue-100 text-blue-800"
                      data-oid="_s8.lms"
                    >
                      {getTranslation(
                        language,
                        "localAttractions.transportation.freeWithGuestCard",
                      )}
                    </Badge>
                  </h4>
                  <p className="text-sm text-gray-700" data-oid="r-ozrvw">
                    {getTranslation(
                      language,
                      "localAttractions.transportation.freeWithGuestCardDesc",
                    )}
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="hotel-shuttles"
            className="bg-[#F6F2EC] rounded-lg border border-gray-200"
            data-oid="k_osi6x"
          >
            <AccordionTrigger
              className="px-6 py-4 hover:no-underline"
              data-oid="5wwiv.p"
            >
              <div className="flex items-center gap-3" data-oid="c:fzxw6">
                <Car className="w-5 h-5 text-[#A38648]" data-oid="n6pzxpx" />
                <span
                  className="font-semibold text-gray-900"
                  data-oid="iw8sk.w"
                >
                  {getTranslation(
                    language,
                    "localAttractions.transportation.hotelShuttles",
                  )}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6" data-oid="vm8_j8q">
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                data-oid="kvs.h0p"
              >
                {shuttleServices.map((service, index) => (
                  <Card
                    key={index}
                    className="border-0 shadow-sm"
                    data-oid="u.4heuc"
                  >
                    <CardHeader className="pb-3" data-oid="iyc.947">
                      <CardTitle
                        className="text-lg text-gray-900 font-playfair"
                        data-oid="6:0k5z-"
                      >
                        {getTranslation(
                          language,
                          `localAttractions.transportation.shuttleServices.${service.serviceKey}.service`,
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0" data-oid="1-2e4qh">
                      <div className="space-y-2 text-sm" data-oid="tdi5ken">
                        <div
                          className="flex items-center gap-2"
                          data-oid="v4c.r.o"
                        >
                          <MapPin
                            className="w-4 h-4 text-[#A38648]"
                            data-oid="7a007nm"
                          />

                          <span className="text-gray-600" data-oid="1_8p9b3">
                            {getTranslation(
                              language,
                              `localAttractions.transportation.shuttleServices.${service.serviceKey}.destinations`,
                            )}
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-2"
                          data-oid="q.vfgmk"
                        >
                          <Clock
                            className="w-4 h-4 text-[#A38648]"
                            data-oid="8::9mwd"
                          />

                          <span className="text-gray-600" data-oid="2l30wun">
                            {getTranslation(
                              language,
                              `localAttractions.transportation.shuttleServices.${service.serviceKey}.schedule`,
                            )}
                          </span>
                        </div>
                        <div
                          className="flex items-center gap-2"
                          data-oid="_d.oczz"
                        >
                          <Bus
                            className="w-4 h-4 text-[#A38648]"
                            data-oid="dvpnc11"
                          />

                          <span className="text-gray-600" data-oid="nitnibf">
                            {getTranslation(
                              language,
                              `localAttractions.transportation.frequencies.${service.frequencyKey}`,
                            )}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div
                className="mt-6 bg-green-50 p-4 rounded-lg"
                data-oid="7xx35c3"
              >
                <h4
                  className="font-semibold text-gray-900 mb-2"
                  data-oid="fpfs4a."
                >
                  {getTranslation(
                    language,
                    "localAttractions.transportation.bookingInfo",
                  )}
                </h4>
                <p className="text-sm text-gray-700 mb-3" data-oid="98-0na1">
                  {getTranslation(
                    language,
                    "localAttractions.transportation.bookingInfoDesc",
                  )}
                </p>
                <div
                  className="flex items-center gap-2 text-sm"
                  data-oid="4w_uo_d"
                >
                  <Phone
                    className="w-4 h-4 text-[#A38648]"
                    data-oid="5z5w5k3"
                  />

                  <span className="font-medium" data-oid="8f5qpra">
                    {getTranslation(
                      language,
                      "localAttractions.transportation.concierge",
                    )}
                  </span>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="driving-directions"
            className="bg-[#F6F2EC] rounded-lg border border-gray-200"
            data-oid="0ypntit"
          >
            <AccordionTrigger
              className="px-6 py-4 hover:no-underline"
              data-oid="ccv-or9"
            >
              <div className="flex items-center gap-3" data-oid="_g:og.4">
                <MapPin className="w-5 h-5 text-[#A38648]" data-oid="q1eu0bl" />
                <span
                  className="font-semibold text-gray-900"
                  data-oid="s91c57x"
                >
                  {getTranslation(
                    language,
                    "localAttractions.transportation.drivingDirections",
                  )}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6" data-oid="wkooyhf">
              <div className="space-y-6" data-oid="ds6bgv6">
                <div data-oid="e4cyha9">
                  <h4
                    className="font-semibold text-gray-900 mb-3"
                    data-oid="59w1rb0"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.fromZurich",
                    )}
                  </h4>
                  <ol
                    className="text-sm text-gray-700 space-y-2 list-decimal list-inside"
                    data-oid="q:om9_j"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.drivingDirectionsZurich",
                    ).map((direction: string, index: number) => (
                      <li key={index} data-oid="iptht6y">
                        {direction}
                      </li>
                    ))}
                  </ol>
                </div>

                <div data-oid="14tt92o">
                  <h4
                    className="font-semibold text-gray-900 mb-3"
                    data-oid="-uh:ima"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.fromGeneva",
                    )}
                  </h4>
                  <ol
                    className="text-sm text-gray-700 space-y-2 list-decimal list-inside"
                    data-oid="23evwnx"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.drivingDirectionsGeneva",
                    ).map((direction: string, index: number) => (
                      <li key={index} data-oid="7p6pmpp">
                        {direction}
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg" data-oid="ng1510-">
                  <h4
                    className="font-semibold text-gray-900 mb-2"
                    data-oid="mze7t1r"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.parkingAtHotel",
                    )}
                  </h4>
                  <p className="text-sm text-gray-700" data-oid="m_evjcb">
                    {getTranslation(
                      language,
                      "localAttractions.transportation.parkingAtHotelDesc",
                    )}
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="accessibility"
            className="bg-[#F6F2EC] rounded-lg border border-gray-200"
            data-oid="obbbdr."
          >
            <AccordionTrigger
              className="px-6 py-4 hover:no-underline"
              data-oid="i1zywqz"
            >
              <div className="flex items-center gap-3" data-oid=":7oid5_">
                <Train className="w-5 h-5 text-[#A38648]" data-oid="hdnoti5" />
                <span
                  className="font-semibold text-gray-900"
                  data-oid="ud:e1zt"
                >
                  {getTranslation(
                    language,
                    "localAttractions.transportation.accessibility",
                  )}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6" data-oid="1-8vcv9">
              <div className="space-y-4" data-oid=":1kn3ps">
                <div data-oid="57iw.0j">
                  <h4
                    className="font-semibold text-gray-900 mb-3"
                    data-oid="0k_epeg"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.wheelchairAccessible",
                    )}
                  </h4>
                  <ul
                    className="text-sm text-gray-700 space-y-2"
                    data-oid=".02bs4-"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.accessibilityAttractions",
                    ).map((attraction: string, index: number) => (
                      <li key={index} data-oid="_.e6a-w">
                        • {attraction}
                      </li>
                    ))}
                  </ul>
                </div>

                <div data-oid="tgw9x1.">
                  <h4
                    className="font-semibold text-gray-900 mb-3"
                    data-oid="eeg_abc"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.mobilityServices",
                    )}
                  </h4>
                  <ul
                    className="text-sm text-gray-700 space-y-2"
                    data-oid="_ib9jto"
                  >
                    {getTranslation(
                      language,
                      "localAttractions.transportation.mobilityServicesList",
                    ).map((service: string, index: number) => (
                      <li key={index} data-oid="oh6fglr">
                        • {service}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg" data-oid="bc_5ysl">
                  <p className="text-sm text-gray-700" data-oid="-wr:z--">
                    <strong data-oid="uwgm8ar">
                      {getTranslation(
                        language,
                        "localAttractions.transportation.contactConcierge",
                      )}
                    </strong>{" "}
                    {getTranslation(
                      language,
                      "localAttractions.transportation.contactConciergeDesc",
                    )}
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
};

export default TransportationInfo;
